const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const isVisibleEvent = (event) =>
  event && event.status !== "CANCELLED" && (event.impact === "HIGH" || event.impact === "MEDIUM");

const zoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const values = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  const renderedAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );
  return renderedAsUtc - date.getTime();
};

const zonedStartOfDayMs = (dateString, timeZone) => {
  const utcGuess = Date.parse(`${dateString}T00:00:00Z`);
  if (!Number.isFinite(utcGuess)) return null;
  const zone = timeZone || "UTC";
  let result = utcGuess - zoneOffsetMs(new Date(utcGuess), zone);
  result = utcGuess - zoneOffsetMs(new Date(result), zone);
  return result;
};

const addCalendarDay = (dateString) => {
  const date = new Date(`${dateString}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

export const eventWindowMs = (event) => {
  if (event?.timePrecision === "EXACT" && Number.isFinite(event.startsAtUtc)) {
    const start = event.startsAtUtc * 1000;
    return { start, end: start + 1 };
  }
  if (event?.timePrecision === "DATE_ONLY" && event.eventDate) {
    const nextDate = addCalendarDay(event.eventDate);
    const start = zonedStartOfDayMs(event.eventDate, event.sourceTimeZone);
    const end = nextDate ? zonedStartOfDayMs(nextDate, event.sourceTimeZone) : null;
    if (Number.isFinite(start) && Number.isFinite(end)) return { start, end };
  }
  return null;
};

export const getUpcomingMacroCount = (events, nowValue = Date.now()) => {
  const now = Number(nowValue);
  const windowEnd = now + DAY_MS;
  return (events || []).filter((event) => {
    if (!isVisibleEvent(event)) return false;
    const window = eventWindowMs(event);
    if (!window) return false;
    return event.timePrecision === "DATE_ONLY"
      ? window.end > now && window.start < windowEnd
      : window.start >= now && window.start < windowEnd;
  }).length;
};

export const groupMacroEvents = (events, nowValue = Date.now()) => {
  const now = Number(nowValue);
  const next24End = now + DAY_MS;
  const weekEnd = now + 7 * DAY_MS;
  const next24Hours = [];
  const laterThisWeek = [];

  (events || [])
    .filter(isVisibleEvent)
    .map((event) => ({ event, window: eventWindowMs(event) }))
    .filter(({ window }) => window && window.end > now && window.start <= weekEnd)
    .sort((left, right) => left.window.start - right.window.start || left.event.title.localeCompare(right.event.title))
    .forEach(({ event, window }) => {
      const inNext24 = event.timePrecision === "DATE_ONLY"
        ? window.end > now && window.start < next24End
        : window.start >= now && window.start < next24End;
      (inNext24 ? next24Hours : laterThisWeek).push(event);
    });

  return { next24Hours, laterThisWeek };
};

export const formatEventTime = (event) => {
  if (event?.timePrecision === "DATE_ONLY") {
    const date = new Date(`${event.eventDate}T00:00:00Z`);
    return {
      primary: new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC"
      }).format(date),
      secondary: "Time TBA"
    };
  }
  const date = new Date(event.startsAtUtc * 1000);
  return {
    primary: new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }).format(date),
    secondary: new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date)
  };
};

export const formatCountdown = (event, nowValue = Date.now()) => {
  if (event?.timePrecision !== "EXACT") return "Time TBA";
  const difference = event.startsAtUtc * 1000 - Number(nowValue);
  if (difference <= 0) return "now";
  const minutes = Math.max(1, Math.floor(difference / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const remainingMinutes = minutes % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${remainingMinutes}m`;
  return `in ${remainingMinutes}m`;
};

export const formatUpdatedAgo = (generatedAt, nowValue = Date.now()) => {
  if (!Number.isFinite(generatedAt)) return "Update unavailable";
  const minutes = Math.max(0, Math.floor((Number(nowValue) - generatedAt * 1000) / 60000));
  if (minutes < 1) return "Updated now";
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `Updated ${hours}h ago`;
};

export const isSnapshotStale = (snapshot, nowValue = Date.now()) =>
  !Number.isFinite(snapshot?.generatedAt)
  || Number(nowValue) - snapshot.generatedAt * 1000 > 12 * HOUR_MS
  || (snapshot?.events || []).some((event) => event.stale);
